import * as esprima from "esprima";
import * as Yup from "yup";

export const getInputValidationRules = (criteriaFunction, inputValidationRules) => {
  let validationRules = null;
  let tokenizedFunction;
  try {
    tokenizedFunction = esprima.parseScript(criteriaFunction.toString());
  } catch {
    // Weird fix to avoid parsing error since Esprima can't parse function declarations
    const functionDeclaration = criteriaFunction.toString();
    const functionExpression = `(${functionDeclaration})`;
    tokenizedFunction = esprima.parseScript(functionExpression);
  }
  try {
    validationRules = {};
    const validationRulesIdentifier = [];

    // Recursively traverse the AST
    const traverse = (node, callback) => {
      callback(node);
      for (let key in node) {
        if (node.hasOwnProperty(key) && typeof node[key] === "object" && node[key] !== null) {
          traverse(node[key], callback);
        }
      }
    };

    traverse(tokenizedFunction, (node) => {
      if (node.type === "Property" && node.key.name === "inputValidationRules") {
        const elements = node.value.elements || [];
        elements.forEach((element) => {
          if (element.type === "MemberExpression" && element.property.type === "Identifier") {
            const ruleName = element.property.name;
            validationRulesIdentifier.push(ruleName);
          }
        });
      }
    });

    if (validationRulesIdentifier.length > 0) {
      validationRulesIdentifier.forEach((ruleName) => {
        validationRules = { ...validationRules, ...inputValidationRules[ruleName] };
      });
    }

    return validationRules;
  } catch (error) {
    return validationRules;
  }
};

export const buildValidationSchema = (validationRules) => {
  let schema;

  // Input type initiation
  if (validationRules?.type === "text") {
    schema = Yup.string().typeError("Input must be a string");
  }

  if (validationRules?.type === "numeric" || validationRules?.type === "money") {
    schema = Yup.number().typeError("Input must be a number");
  }

  // Other validation rules

  if (validationRules.required) {
    schema = schema.required("This field is required");
  }

  if (validationRules?.min) {
    schema = schema.min(validationRules?.min, `Minimum value allowed is ${validationRules?.min}`);
  }

  if (validationRules?.max) {
    schema = schema.max(validationRules?.max, `Maximum value allowed is ${validationRules?.max}`);
  }

  if (validationRules.positive) {
    schema = schema.positive("Only positive integer allowed");
  }

  return schema;
};

export const inputValidationRules = {
  text: {
    type: "text",
    minLength: 3,
    maxLength: 100,
  },
  numeric: {
    type: "numeric",
    positive: true,
    max: 9999,
    maxLength: 4,
  },
  money: {
    type: "money", // This isn't actually a valid prop for text input, used only to use the existing NumericFormatCustom component
    positive: true,
    maxLength: 15,
  },
  required: {
    required: true,
  },
  noDecimal: {
    decimalScale: 0, // Limits the number of digits after the decimal point, 0 = no decimal point
  },
};

const criteria = {
  standard: {
    label: "Standard",
    object: "asset",
    attribute: "assetValue",
    condition: "is",
    value: true,
  },
  // propertyOwner: {
  //   label: "Property owner",
  //   object: "customerProfile",
  //   attribute: "livingArrangements",
  //   condition: "exists",
  //   value: "Property Owner",
  // },
  // hasDefault: {
  //   label: "Has default",
  //   object: "creditProfile",
  //   attribute: "hasAdverseCredit",
  //   condition: "exists",
  //   value: true,
  // },
  // noDefault: {
  //   label: "Has default",
  //   object: "creditProfile",
  //   attribute: "hasAdverseCredit",
  //   condition: "doesntExist",
  //   value: false,
  // },
  dateOfBirthLessThan: (value) => ({
    label: `Age of customer <${value} years`,
    object: "customer",
    attribute: "dateOfBirth",
    condition: "lessThan",
    value,
    inputValidationRules: [inputValidationRules.numeric, inputValidationRules.required],
  }),
  dateOfBirthGreaterThan: (value) => ({
    label: `Age of customer >${value} years`,
    object: "customer",
    attribute: "dateOfBirth",
    condition: "greaterThan",
    value,
  }),
  dateOfBirthBetween: (min, max) => ({
    label: `Age of customer ${min}-${max} years`,
    object: "customer",
    attribute: "dateOfBirth",
    condition: "betweenLow",
    value: {
      min,
      max,
    },
    inputValidationRules: [inputValidationRules.numeric, inputValidationRules.required],
  }),
  nonGST: {
    label: "Not GST registered",
    object: "entity",
    attribute: "gst",
    condition: "isNot",
  },
  termIsLessThan: (value) => ({
    label: `Term <${value} months`,
    object: "loanDetails",
    attribute: "term",
    condition: "lessThan",
    value,
  }),
  termIsBetween: (min, max) => ({
    label: `Term ${min} - ${max} months`,
    object: "loanDetails",
    attribute: "term",
    condition: "betweenLow",
    value: {
      min,
      max,
    },
  }),
  termIsLessThanOrEqualTo: (value) => ({
    label: `Term max ${value} months`,
    object: "loanDetails",
    attribute: "term",
    condition: "lessThanOrEqualTo",
    value,
  }),
  termIsGreaterThan: (value) => ({
    label: `Term >${value} months`,
    object: "loanDetails",
    attribute: "term",
    condition: "greaterThan",
    value,
  }),
  // depositGreaterThanOrEqualTo: (value) => ({
  //   label: "20% deposit",
  //   object: "loanDetails",
  //   attribute: "deposit",
  //   condition: "greaterThanOrEqualTo",
  //   value,
  // }),
  // nonPropertyOwner: {
  //   label: "Not a property owner",
  //   object: "customerProfile",
  //   attribute: "livingArrangements",
  //   condition: "doesntExist",
  //   value: "Property Owner",
  // },
  eachCreditScoreGreaterThan: (value) => ({
    label: `Credit score >${value}`,
    object: "creditProfile",
    attribute: "score",
    condition: "eachIsGreaterThan",
    value,
  }),
  creditScoreGreaterThan: (value) => ({
    label: `Credit score >${value}`,
    object: "creditProfile",
    attribute: "score",
    condition: "greaterThan",
    value,
  }),
  creditScoreLessThan: (value) => ({
    label: `Credit score <${value}`,
    object: "creditProfile",
    attribute: "score",
    condition: "lessThan",
    value,
  }),
  creditScoreBetween: (min, max) => ({
    label: `Credit score ${min} - ${max}`,
    object: "creditProfile",
    attribute: "score",
    condition: "between",
    value: {
      min,
      max,
    },
  }),
  employmentType: (value) => ({
    label: "Employment type",
    object: "employer",
    attribute: "employmentType",
    condition: "hasOne",
    value,
  }),
  timeOfEmploymentGreaterThan: (value) => ({
    label: `Employed >${value} months`,
    object: "employer",
    attribute: "timeOfEmployment",
    condition: "greaterThan",
    value,
  }),
  livingArrangements: (value) => ({
    label: "Living arrangements",
    object: "customerProfile",
    attribute: "livingArrangements",
    condition: "hasOne",
    value,
  }),
  typeOfSale: (value) => ({
    label: "Type of sale",
    object: "asset",
    attribute: "typeOfSale",
    condition: "hasOne",
    value,
  }),
  typeOfSaleMany: (value) => ({
    label: "Type of sale",
    object: "asset",
    attribute: "typeOfSale",
    condition: "hasOne",
    value,
  }),
  hasDeposit: (value) =>
  ({
    label: `${value}% deposit`,
    object: "loanDetails",
    attribute: "deposit",
    condition: "greaterThanOrEqualTo",
    value,
  }),
  depositBetween: (min, max) => ({
    label: `Deposit ${min}-${max}% `,
    object: "loanDetails",
    attribute: "deposit",
    condition: "between",
    value: {
      min,
      max,
    },
  }),
  noDeposit: (value) => ({
    label: `No deposit`,
    object: "loanDetails",
    attribute: "deposit",
    condition: "zero",
    value,
  }),
  balloonLessThan: function (value) {
    return {
      label: `Balloon <${value}%`,
      object: "loanDetails",
      attribute: "balloon",
      condition: "lessThan",
      value,
    };
  },
  balloonGreaterThan: function (value) {
    return {
      label: `Balloon >${value}%`,
      object: "loanDetails",
      attribute: "balloon",
      condition: "greaterThan",
      value,
    };
  },
  balloonBetween: (min, max) => ({
    label: `Balloon ${min}-${max}% `,
    object: "loanDetails",
    attribute: "balloon",
    condition: "between",
    value: {
      min,
      max,
    },
  }),
  assetValueGreaterThan: function (value) {
    return {
      label: `Asset value >$${value}`,
      object: "asset",
      attribute: "assetValue",
      condition: "greaterThan",
      value,
      inputValidationRules: [inputValidationRules.money, inputValidationRules.required],
    };
  },
  assetValueBetween: function (min, max) {
    return {
      label: `Asset value $${min} - $${max}`,
      object: "asset",
      attribute: "assetValue",
      condition: "between",
      value: {
        min,
        max,
      },
    };
  },
  assetType: function (value) {
    return {
      label: "Asset Type",
      object: "asset",
      attribute: "assetType",
      condition: "hasOne",
      value,
    };
  },
  assetCondition: function (value) {
    return {
      label: "Asset Condition",
      object: "asset",
      attribute: "condition",
      condition: "hasOne",
      value,
    };
  },
  // ageOfAssetNewOrDemo: {
  //   label: `Asset New/Demo`,
  //   object: "asset",
  //   attribute: "ageOfAsset",
  //   condition: "hasOne",
  //   value: ["New", "Demo"],
  // },
  ageOfAssetGreaterThan: function (value) {
    return {
      label: `Asset age >${value} mths`,
      object: "asset",
      attribute: "ageOfAsset",
      condition: "greaterThan",
      value,
    };
  },
  ageOfAssetGreaterThanOrEqualTo: function (value) {
    return {
      label: `Asset age >${value} mths`,
      object: "asset",
      attribute: "ageOfAsset",
      condition: "greaterThanOrEqualTo",
      value,
    };
  },
  ageOfAssetLessThan: function (value) {
    return {
      label: `Asset age <${value} mths`,
      object: "asset",
      attribute: "ageOfAsset",
      condition: "lessThan",
      value,
    };
  },
  ageOfAssetLessThanOrEqualTo: function (value) {

    return {
      label: `Asset age <${value} mths`,
      object: "asset",
      attribute: "ageOfAsset",
      condition: "lessThanOrEqualTo",
      value,
    };
  },
  ageOfAssetBetween: function (min, max) {
    return {
      label: `Asset age ${min} - ${max} mths`,
      object: "asset",
      attribute: "ageOfAsset",
      condition: "betweenLow",
      value: {
        min,
        max,
      },
    };
  },
  ageOfAssetAtEndGreaterThan: function (value) {
    return {
      label: `End of term age >${value} yrs`,
      object: "asset",
      attribute: "ageOfAssetAtEnd",
      condition: "greaterThan",
      value,
    };
  },
  ageOfAssetAtEndLessThan: function (value) {
    return {
      label: `End of term age <${value} yrs`,
      object: "asset",
      attribute: "ageOfAssetAtEnd",
      condition: "lessThan",
      value,
    };
  },
  ageOfAssetAtEndBetween: function (min, max) {
    return {
      label: `End of term age ${min} - ${max}yrs`,
      object: "asset",
      attribute: "ageOfAssetAtEnd",
      condition: "between",
      value: {
        min,
        max,
      },
    };
  },
  loanAmountGreaterThan: function (value) {
    return {
      label: `Loan amount >$${value}`,
      object: "loanDetails",
      attribute: "loanAmount",
      condition: "greaterThan",
      value,
    };
  },
  loanAmountLessThan: function (value) {
    return {
      label: `Loan amount <$${value}`,
      object: "loanDetails",
      attribute: "loanAmount",
      condition: "lessThan",
      value,
    };
  },
  loanAmountBetween: function (min, max) {
    return {
      label: `Loan amount $${min} - $${max}`,
      object: "loanDetails",
      attribute: "loanAmount",
      condition: "betweenLow",
      value: {
        min,
        max,
      },
      inputValidationRules: [inputValidationRules.money, inputValidationRules.required],
    };
  },
  loanPurpose: function (value) {
    return {
      label: `Loan purpose: ${value}`,
      object: "loanDetails",
      attribute: "purpose",
      condition: "hasOne",
      value,
    };
  },
  turnoverGreaterThan: function (value) {
    return {
      label: `Turnover >$${value}`,
      object: "entity",
      attribute: "turnover",
      condition: "greaterThan",
      value,
    };
  },
  turnoverLessThan: function (value) {
    return {
      label: `Turnover <$${value}`,
      object: "entity",
      attribute: "turnover",
      condition: "lessThan",
      value,
    };
  },
  turnoverBetween: function (min, max) {
    return {
      label: `Turnover $${min} - $${max}`,
      object: "entity",
      attribute: "turnover",
      condition: "betweenLow",
      value: {
        min,
        max,
      },
      inputValidationRules: [inputValidationRules.money, inputValidationRules.required],
    };
  },
  timeInBusinessBetween: function (min, max) {
    return {
      label: `Time in business ${min} - ${max} mths`,
      object: "entity",
      attribute: "timeInBusiness",
      condition: "betweenLow",
      value: {
        min: min,
        max: max,
      },
      inputValidationRules: [inputValidationRules.numeric, inputValidationRules.required],
    };
  },
  timeInBusinessLessThan: function (value) {
    return {
      label: `Time in business <${value} mths`,
      object: "entity",
      attribute: "timeInBusiness",
      condition: "lessThan", // "greaterThan",
      value
    };
  },
  timeInBusinessGreaterThan: function (value) {
    return {
      label: `Time in business >${value} mths`,
      object: "entity",
      attribute: "timeInBusiness",
      condition: "greaterThan", // "lessThan",
      value
    };
  },
  gstRegistrationGreaterThan: function (value) {
    return {
      label: `GST reg. >${value} mths`,
      object: "entity",
      attribute: "gst",
      condition: "greaterThan", // lessThan
      value
    };
  },
  gstRegistrationLessThan: function (value) {
    return {
      label: `GST reg. <${value} mths`,
      object: "entity",
      attribute: "gst",
      condition: "lessThan", // greaterThan
      value
    };
  },
  gstRegistrationBetween: function (min, max) {
    return {
      label: `GST reg. ${min} - ${max} mths`,
      object: "entity",
      attribute: "gst",
      condition: "betweenLow",
      value: {
        min,
        max
      },
    };
  },
  customerDefaultAmountLessThan: (value) => ({
    label: `Customer defaults value <$${value}`,
    object: "customer",
    attribute: "defaultValue",
    condition: "lessThan",
    value: value,
  }),
  brokerageAmount: (min, max) => ({
    label: `Brokerage amount`,
    object: "loanDetails",
    attribute: "brokerageRate",
    condition: "betweenLow",
    value: {
      min,
      max,
    },
  }),
  brokerageIsEqualTo: (value) => ({
    label: `Brokerage`,
    object: "loanDetails",
    attribute: "brokerage",
    condition: "equal",
    value,
  }),
  brokerageIsBetween: (min, max) => ({
    label: `Brokerage`,
    object: "loanDetails",
    attribute: "brokerage",
    condition: "betweenLow",
    value: {
      min,
      max,
    },
  }),
  professionalServices: (value) => ({
    label: "Professional Services",
    object: "entity",
    attribute: "professionalServices",
    condition: "hasOne",
    value,
  }),
};
export default criteria;
